import type { ZwChapterNumbering } from "../lib/zwConfig";
/**
 * Pure TEA update for Project Settings draft state.
 *
 * Shell: `ProjectSettingsDialog.vue` (cover preview / asset I/O / focus stay
 * in the shell; save emit uses `buildProjectSettingsSavePayload`).
 */
import { assertNever } from "../lib/assertNever";
import { customRelationId } from "../lib/entityLinks";
import {
  fromMaybe,
  isJust,
  matchMaybe,
  nothing,
  toMaybe,
  type Maybe,
} from "../lib/maybe";
import { uniqueDictionaryWords } from "../lib/projectDictionary";
import type { UpdateFn } from "../lib/tea";
import { parseGoalDraft } from "../lib/writingGoals";
import type {
  ZwChapterHeadingStyle,
  ZwConfig,
  ZwCopyrightPage,
  ZwDatabaseFieldDef,
  ZwDatabaseRelationPair,
  ZwDedicationPage,
  ZwFonts,
  ZwGoals,
  ZwMetadata,
  ZwPageChrome,
  ZwParagraphFormatting,
  ZwPrintSize,
  ZwSceneFormatting,
  ZwStatusBar,
} from "../lib/zwConfig";
import {
  DEFAULT_PRINT_SIZE,
  defaultZwChapterHeadingStyle,
  defaultZwConfig,
  defaultZwCopyrightPage,
  defaultZwDedicationPage,
  defaultZwGoals,
  defaultZwMetadata,
  defaultZwPageChrome,
  defaultZwParagraphFormatting,
  defaultZwSceneFormatting,
  defaultZwStatusBar,
  normalizeZwGoals,
} from "../lib/zwConfig";
import type { ProjectSettingsSectionId } from "./projectSettingsSectionId";

export type ProjectSettingsDraftModel = {
  readonly section: ProjectSettingsSectionId;
  readonly fonts: ZwFonts;
  readonly printSize: ZwPrintSize;
  readonly paragraphFormatting: ZwParagraphFormatting;
  readonly chapterHeadingStyle: ZwChapterHeadingStyle;
  readonly chapterNumbering: ZwChapterNumbering;
  readonly actNumbering: ZwChapterNumbering;
  readonly pageChrome: ZwPageChrome;
  readonly sceneFormatting: ZwSceneFormatting;
  readonly copyright: ZwCopyrightPage;
  readonly dedication: ZwDedicationPage;
  readonly meta: ZwMetadata;
  readonly dictionary: string[];
  readonly statusBar: ZwStatusBar;
  readonly databaseFields: ZwDatabaseFieldDef[];
  readonly databaseRelationPairs: ZwDatabaseRelationPair[];
  readonly sessionWords: string;
  readonly dailyWords: string;
  readonly projectWords: string;
  /** Remount key for goal ZwInputs after seed. */
  readonly goalsFieldsKey: number;
  readonly dictionaryAddDraft: string;
  readonly coverPreviewSrc: string;
};

export type ProjectSettingsSavePayload = {
  readonly fonts: ZwFonts;
  readonly printSize: ZwPrintSize;
  readonly paragraphFormatting: ZwParagraphFormatting;
  readonly chapterHeadingStyle: ZwChapterHeadingStyle;
  readonly chapterNumbering: ZwChapterNumbering;
  readonly actNumbering: ZwChapterNumbering;
  readonly pageChrome: ZwPageChrome;
  readonly sceneFormatting: ZwSceneFormatting;
  readonly copyrightPage: ZwCopyrightPage;
  readonly dedicationPage: ZwDedicationPage;
  readonly metadata: ZwMetadata;
  readonly dictionary: string[];
  readonly statusBar: ZwStatusBar;
  readonly goals: ZwGoals;
  readonly databaseFields: ZwDatabaseFieldDef[];
  readonly databaseRelationPairs: ZwDatabaseRelationPair[];
};

export function sortDictionaryWords(words: string[]): string[] {
  return uniqueDictionaryWords(words).sort((a, b) =>
    a.localeCompare(b, "en", { sensitivity: "base" }),
  );
}

export function goalDraftFromConfig(goals: Maybe<ZwGoals>): {
  readonly session: string;
  readonly daily: string;
  readonly project: string;
} {
  const normalized = normalizeZwGoals(
    matchMaybe(goals, {
      just: (g) => g,
      nothing: () => defaultZwGoals(),
    }),
  );
  return {
    session: isJust(toMaybe(normalized.sessionWords))
      ? String(normalized.sessionWords)
      : "",
    daily: isJust(toMaybe(normalized.dailyWords))
      ? String(normalized.dailyWords)
      : "",
    project: isJust(toMaybe(normalized.projectWords))
      ? String(normalized.projectWords)
      : "",
  };
}

export function seedProjectSettingsDraft(
  config: ZwConfig,
  initialSection: Maybe<ProjectSettingsSectionId>,
  previousGoalsKey: number,
): ProjectSettingsDraftModel {
  const goalDrafts = goalDraftFromConfig(toMaybe(config.goals));
  return {
    section: matchMaybe(initialSection, {
      just: (id) => id,
      nothing: () => "metadata" as ProjectSettingsSectionId,
    }),
    fonts: { ...config.fonts },
    printSize: config.printSize ?? DEFAULT_PRINT_SIZE,
    paragraphFormatting: {
      ...defaultZwParagraphFormatting(),
      ...config.paragraphFormatting,
    },
    chapterNumbering: { ...config.chapterNumbering },
    actNumbering: { ...config.actNumbering },
    chapterHeadingStyle: {
      ...defaultZwChapterHeadingStyle(),
      ...config.chapterHeadingStyle,
    },
    pageChrome: {
      ...defaultZwPageChrome(),
      ...config.pageChrome,
    },
    sceneFormatting: {
      ...defaultZwSceneFormatting(),
      ...config.sceneFormatting,
    },
    copyright: {
      ...defaultZwCopyrightPage(),
      ...config.copyrightPage,
      credits: [...(config.copyrightPage?.credits ?? [])].map((c) => ({
        ...c,
      })),
    },
    dedication: {
      ...defaultZwDedicationPage(),
      ...config.dedicationPage,
    },
    meta: { ...defaultZwMetadata(), ...config.metadata },
    dictionary: sortDictionaryWords(config.dictionary ?? []),
    statusBar: {
      ...defaultZwStatusBar(),
      ...config.statusBar,
    },
    databaseFields: (config.databaseFields ?? []).map((f) => ({
      ...f,
      options: [...f.options],
    })),
    databaseRelationPairs: (config.databaseRelationPairs ?? []).map((p) => ({
      ...p,
    })),
    sessionWords: goalDrafts.session,
    dailyWords: goalDrafts.daily,
    projectWords: goalDrafts.project,
    goalsFieldsKey: previousGoalsKey + 1,
    dictionaryAddDraft: "",
    coverPreviewSrc: "",
  };
}

export const initialProjectSettingsDraftModel: ProjectSettingsDraftModel =
  seedProjectSettingsDraft(defaultZwConfig(), nothing, -1);

export type ProjectSettingsDraftMsg =
  | {
      readonly type: "SeedFromConfig";
      readonly config: ZwConfig;
      readonly initialSection: Maybe<ProjectSettingsSectionId>;
    }
  | {
      readonly type: "SectionChanged";
      readonly section: ProjectSettingsSectionId;
    }
  | {
      readonly type: "ActNumberingChanged";
      readonly actNumbering: ZwChapterNumbering;
    }
  | { readonly type: "FontsChanged"; readonly fonts: ZwFonts }
  | { readonly type: "PrintSizeChanged"; readonly printSize: ZwPrintSize }
  | {
      readonly type: "ParagraphFormattingChanged";
      readonly paragraphFormatting: ZwParagraphFormatting;
    }
  | {
      readonly type: "ChapterNumberingChanged";
      readonly chapterNumbering: ZwChapterNumbering;
    }
  | {
      readonly type: "ChapterHeadingStyleChanged";
      readonly chapterHeadingStyle: ZwChapterHeadingStyle;
    }
  | { readonly type: "PageChromeChanged"; readonly pageChrome: ZwPageChrome }
  | {
      readonly type: "SceneFormattingChanged";
      readonly sceneFormatting: ZwSceneFormatting;
    }
  | { readonly type: "CopyrightChanged"; readonly copyright: ZwCopyrightPage }
  | {
      readonly type: "DedicationChanged";
      readonly dedication: ZwDedicationPage;
    }
  | { readonly type: "MetaChanged"; readonly meta: ZwMetadata }
  | { readonly type: "DictionaryChanged"; readonly dictionary: string[] }
  | { readonly type: "StatusBarChanged"; readonly statusBar: ZwStatusBar }
  | {
      readonly type: "DatabaseFieldsChanged";
      readonly databaseFields: ZwDatabaseFieldDef[];
    }
  | {
      readonly type: "DatabaseRelationPairsChanged";
      readonly databaseRelationPairs: ZwDatabaseRelationPair[];
    }
  | { readonly type: "SessionWordsChanged"; readonly sessionWords: string }
  | { readonly type: "DailyWordsChanged"; readonly dailyWords: string }
  | { readonly type: "ProjectWordsChanged"; readonly projectWords: string }
  | {
      readonly type: "DictionaryAddDraftChanged";
      readonly dictionaryAddDraft: string;
    }
  | {
      readonly type: "SeedGoalsIfEmpty";
      readonly goals: Maybe<ZwGoals>;
    }
  | { readonly type: "SetCoverPreviewSrc"; readonly src: string };

export type ProjectSettingsDraftCmd =
  | {
      readonly kind: "focusSection";
      readonly section: ProjectSettingsSectionId;
    }
  | { readonly kind: "refreshCoverPreview" };

export function updateProjectSettingsDraft(
  model: ProjectSettingsDraftModel,
  msg: ProjectSettingsDraftMsg,
): readonly [ProjectSettingsDraftModel, readonly ProjectSettingsDraftCmd[]] {
  switch (msg.type) {
    case "SeedFromConfig": {
      const next = seedProjectSettingsDraft(
        msg.config,
        msg.initialSection,
        model.goalsFieldsKey,
      );
      return [
        next,
        [
          { kind: "focusSection", section: next.section },
          { kind: "refreshCoverPreview" },
        ],
      ];
    }
    case "SectionChanged":
      return [
        { ...model, section: msg.section },
        [{ kind: "focusSection", section: msg.section }],
      ];
    case "FontsChanged":
      return [{ ...model, fonts: msg.fonts }, []];
    case "PrintSizeChanged":
      return [{ ...model, printSize: msg.printSize }, []];
    case "ParagraphFormattingChanged":
      return [{ ...model, paragraphFormatting: msg.paragraphFormatting }, []];
    case "ActNumberingChanged":
      return [{ ...model, actNumbering: msg.actNumbering }, []];
    case "ChapterNumberingChanged":
      return [{ ...model, chapterNumbering: msg.chapterNumbering }, []];
    case "ChapterHeadingStyleChanged":
      return [{ ...model, chapterHeadingStyle: msg.chapterHeadingStyle }, []];
    case "PageChromeChanged":
      return [{ ...model, pageChrome: msg.pageChrome }, []];
    case "SceneFormattingChanged":
      return [{ ...model, sceneFormatting: msg.sceneFormatting }, []];
    case "CopyrightChanged":
      return [{ ...model, copyright: msg.copyright }, []];
    case "DedicationChanged":
      return [{ ...model, dedication: msg.dedication }, []];
    case "MetaChanged": {
      const coverChanged =
        model.meta.coverImage.trim() !== msg.meta.coverImage.trim();
      return [
        { ...model, meta: msg.meta },
        coverChanged ? [{ kind: "refreshCoverPreview" }] : [],
      ];
    }
    case "DictionaryChanged":
      return [{ ...model, dictionary: msg.dictionary }, []];
    case "StatusBarChanged":
      return [{ ...model, statusBar: msg.statusBar }, []];
    case "DatabaseFieldsChanged":
      return [{ ...model, databaseFields: msg.databaseFields }, []];
    case "DatabaseRelationPairsChanged":
      return [
        { ...model, databaseRelationPairs: msg.databaseRelationPairs },
        [],
      ];
    case "SessionWordsChanged":
      return [{ ...model, sessionWords: msg.sessionWords }, []];
    case "DailyWordsChanged":
      return [{ ...model, dailyWords: msg.dailyWords }, []];
    case "ProjectWordsChanged":
      return [{ ...model, projectWords: msg.projectWords }, []];
    case "DictionaryAddDraftChanged":
      return [{ ...model, dictionaryAddDraft: msg.dictionaryAddDraft }, []];
    case "SeedGoalsIfEmpty": {
      if (
        model.sessionWords.length > 0 ||
        model.dailyWords.length > 0 ||
        model.projectWords.length > 0
      ) {
        return [model, []];
      }
      const goalDrafts = goalDraftFromConfig(msg.goals);
      if (
        goalDrafts.session.length === 0 &&
        goalDrafts.daily.length === 0 &&
        goalDrafts.project.length === 0
      ) {
        return [model, []];
      }
      return [
        {
          ...model,
          sessionWords: goalDrafts.session,
          dailyWords: goalDrafts.daily,
          projectWords: goalDrafts.project,
          goalsFieldsKey: model.goalsFieldsKey + 1,
        },
        [],
      ];
    }
    case "SetCoverPreviewSrc":
      return [{ ...model, coverPreviewSrc: msg.src }, []];
    default:
      return assertNever(msg);
  }
}

export function buildProjectSettingsSavePayload(
  model: ProjectSettingsDraftModel,
): ProjectSettingsSavePayload {
  const metadata = { ...model.meta };
  metadata.title = metadata.title.trim();
  metadata.subtitle = metadata.subtitle.trim();
  metadata.author = metadata.author.trim();
  const language = metadata.language.trim();
  metadata.language = language.length > 0 ? language : "en";
  metadata.description = metadata.description.trim();
  metadata.publisher = metadata.publisher.trim();
  metadata.rights = metadata.rights.trim();
  metadata.isbn = metadata.isbn.trim();
  metadata.lccn = metadata.lccn.trim();
  metadata.subject = metadata.subject.trim();
  metadata.pubDate = metadata.pubDate.trim();
  metadata.coverImage = metadata.coverImage.trim();

  return {
    fonts: { ...model.fonts },
    printSize: model.printSize,
    paragraphFormatting: { ...model.paragraphFormatting },
    chapterNumbering: { ...model.chapterNumbering },
    actNumbering: { ...model.actNumbering },
    chapterHeadingStyle: {
      ...model.chapterHeadingStyle,
      image: model.chapterHeadingStyle.image.trim(),
    },
    pageChrome: { ...model.pageChrome },
    sceneFormatting: { ...model.sceneFormatting },
    copyrightPage: {
      ...model.copyright,
      editionLine: model.copyright.editionLine.trim(),
      publishedBy: model.copyright.publishedBy.trim(),
      authorUrl: model.copyright.authorUrl.trim(),
      additionalText: model.copyright.additionalText.trim(),
      credits: model.copyright.credits
        .map((c) => ({
          role: c.role.trim(),
          name: c.name.trim(),
        }))
        .filter((c) => c.role.length > 0 || c.name.length > 0),
    },
    dedicationPage: {
      enabled: model.dedication.enabled,
      text: model.dedication.text.trim(),
    },
    metadata,
    dictionary: sortDictionaryWords(model.dictionary),
    statusBar: { ...model.statusBar },
    goals: {
      sessionWords: fromMaybe(parseGoalDraft(model.sessionWords)),
      dailyWords: fromMaybe(parseGoalDraft(model.dailyWords)),
      projectWords: fromMaybe(parseGoalDraft(model.projectWords)),
    },
    databaseFields: model.databaseFields
      .map((f) => ({ ...f, name: f.name.trim() }))
      .filter((f) => f.name.length > 0),
    databaseRelationPairs: model.databaseRelationPairs
      .map((pair) => {
        const aLabel = pair.aLabel.trim();
        const bLabelTrim = pair.bLabel.trim();
        const bLabel = bLabelTrim.length > 0 ? bLabelTrim : aLabel;
        const aIdTrim = pair.aId.trim();
        const aIdFromLabelM = customRelationId(aLabel);
        const aId =
          aIdTrim.length > 0
            ? aIdTrim
            : isJust(aIdFromLabelM)
              ? aIdFromLabelM.value
              : aIdTrim;
        const bIdTrim = pair.bId.trim();
        const bIdFromLabelM = customRelationId(bLabel);
        const bId =
          bIdTrim.length > 0
            ? bIdTrim
            : isJust(bIdFromLabelM)
              ? bIdFromLabelM.value
              : aId;
        return {
          id: pair.id,
          aId,
          aLabel,
          bId: bLabel === aLabel ? aId : bId,
          bLabel,
        };
      })
      .filter((pair) => pair.aLabel.length > 0 && pair.aId.length > 0),
  };
}

export const updateProjectSettingsDraftFn: UpdateFn<
  ProjectSettingsDraftModel,
  ProjectSettingsDraftMsg,
  ProjectSettingsDraftCmd
> = updateProjectSettingsDraft;
